import React, { useEffect, useState } from "react";
import axios from "axios";
import { Redirect } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

const ACCENT = COLORS.primary_color_shade6 || "#039BE5";
const MODAL_RADIUS = 16;

const MODAL_STYLES = `
  .mdl-change-session-cycle.modal-dialog {
    margin: 0.75rem auto 1.25rem;
    display: flex;
    align-items: center;
  }
  .mdl-change-session-cycle .modal-content {
    border: none;
    border-radius: ${MODAL_RADIUS}px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  }
  .mdl-change-session-cycle .form-control {
    border-radius: 10px;
    border: 1px solid #e5e7eb;
  }
  .mdl-change-session-cycle .modal-header .btn-close {
    filter: brightness(0) invert(1);
    opacity: 0.9;
  }
`;

function toInputDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

const MdlChangeSessionCycle = ({ actionClose, action2, session }) => {
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (session) {
      setStartDate(toInputDate(session.start_date));
      setEndDate(toInputDate(session.end_date));
    }
  }, [session]);

  const handleClose = () => {
    setShow(false);
    actionClose();
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError("End date cannot be before start date.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id;
      if (!staffId) {
        myToast.notify_warning("Staff session not found. Please log in again.");
        return;
      }

      const url =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "changeSessionCycle.php";

      const response = await axios.get(url, {
        params: {
          school_code: schoolCode,
          staff_id: staffId,
          session_id: session.session_id,
          start_date: startDate,
          end_date: endDate,
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success(
          response.data.api_remark || "Session cycle updated successfully!"
        );
        if (typeof action2 === "function") action2();
        handleClose();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not update session cycle."
        );
      }
    } catch (err) {
      console.error("Change session cycle error:", err);
      myToast.notify_error("An error occurred while updating session cycle.");
    } finally {
      setLoading(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <>
      <style>{MODAL_STYLES}</style>
      <Modal
        show={show}
        onHide={handleClose}
        animation={false}
        size="lg"
        dialogClassName="mdl-change-session-cycle"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%)`,
            borderBottom: "none",
            color: "#fff",
          }}
        >
          <Modal.Title style={{ fontWeight: 800, color: "#fff" }}>
            <i className="fa fa-refresh" style={{ marginRight: 8 }} />
            Change Session Cycle
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ paddingTop: 18 }}>
          {loading ? (
            <MyLoader />
          ) : (
            <div className="row g-3">
              <div className="col-md-12">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0369a1",
                    padding: "8px 12px",
                    borderRadius: 10,
                    background: "color-mix(in srgb, #0ea5e9 10%, #ffffff)",
                    border: "1px solid color-mix(in srgb, #0ea5e9 22%, #e5e7eb)",
                  }}
                >
                  <i className="fa fa-info-circle" style={{ marginRight: 6 }} />
                  Updating cycle for: <strong>{session?.session_name || "—"}</strong>
                </div>
              </div>

              <div className="col-md-6">
                <Form.Label style={{ fontWeight: 700, fontSize: 13 }}>
                  Start Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <Form.Label style={{ fontWeight: 700, fontSize: 13 }}>
                  End Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              {error && (
                <div className="col-md-12" style={{ color: "#dc2626", fontSize: 12 }}>
                  {error}
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="d-flex justify-content-end w-100 gap-2">
            <Button variant="light" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              style={{ background: ACCENT, borderColor: ACCENT, fontWeight: 700 }}
            >
              Save Cycle
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MdlChangeSessionCycle;
